import { Injectable, signal, inject, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { OfflineStorageService } from './offline-storage.service';
import { firstValueFrom } from 'rxjs';

export interface CommentNotification {
  commentID: number;
  passportID: number | string;
  loadNumber?: string;
  username?: string;
  employeeName?: string;
  userRole?: string;
  commentText?: string;
  createdOn?: string;
  isReadByCurrentUser?: boolean | null;
}

@Injectable({
  providedIn: 'root'
})
export class CommentsNotificationService {
  private readonly http = inject(HttpClient);
  private readonly offlineStorage = inject(OfflineStorageService);
  
  // Signal to hold the total comment count from the last 24 hours
  public recentCount = signal<number>(0);
  // Back-compat alias so existing bindings using unreadCount() keep working
  public unreadCount = computed(() => this.recentCount());
  
  // Signal to hold all comments from the last 24 hours
  private readonly comments = signal<CommentNotification[]>([]);
  
  // Flag to prevent multiple simultaneous fetches
  private isFetching = false;

  // Track last user to reset state on account switch within same session
  private lastUserId: number | null = null;

  // --- Persistence helpers (per-user) ---
  private getStorageKey(userId: number): string {
    return `recentCount:${userId}`;
  }

  private saveUnreadForUser(userId: number, count: number): void {
    try {
      localStorage.setItem(this.getStorageKey(userId), String(count));
    } catch {
      // Ignore localStorage errors (e.g., in private browsing mode)
    }
  }

  private loadUnreadForUser(userId: number): number | null {
    try {
      // Prefer new key but fall back to legacy key for one-time migration
      let v = localStorage.getItem(this.getStorageKey(userId));
      v ??= localStorage.getItem(`unreadCount:${userId}`);
      if (v == null) return null;
      const n = Number.parseInt(v, 10);
      return Number.isFinite(n) ? n : null;
    } catch {
      return null;
    }
  }

  constructor() {
    // Capture initial user ID (if present)
    const initialUserIdRaw = localStorage.getItem('userID');
    const initialUserId = initialUserIdRaw ? Number.parseInt(initialUserIdRaw, 10) : null;
    const isValidInitialUserId = typeof initialUserId === 'number' && Number.isFinite(initialUserId);
    this.lastUserId = isValidInitialUserId ? initialUserId : null;

    // Seed from persisted value for immediate UI feedback
    if (this.lastUserId != null) {
      const seeded = this.loadUnreadForUser(this.lastUserId);
      if (seeded != null) {
        this.recentCount.set(seeded);
      }
    }
  }

  /**
   * Initialize the service by fetching the latest unread count
   * Should be called after service injection (e.g., in ngOnInit)
   */
  initialize(): void {
    void this.fetchUnreadCount().catch(err => {
      console.warn('Initial comment count fetch failed:', err);
    });
  }

  /**
   * Fetches unread comments from the last 24 hours for the current user
   * Uses cached data when offline
   */
  async fetchUnreadCount(): Promise<void> {
    if (this.isFetching) {
      return; // Prevent concurrent fetches
    }

    const userID = localStorage.getItem('userID');
    const token = localStorage.getItem('accessToken');
    const tokenType = localStorage.getItem('tokenType') || 'Bearer';
    
    if (!userID || !token) {
      console.warn('Missing authentication data for fetching comment notifications');
      this.recentCount.set(0);
      this.comments.set([]);
      this.lastUserId = null;
      return;
    }

    const numericUserId = Number.parseInt(userID, 10);

    // Reset state if user changed within same SPA session
    if (!Number.isFinite(numericUserId)) {
      this.recentCount.set(0);
      this.comments.set([]);
      this.lastUserId = null;
      return;
    }

    if (this.lastUserId !== numericUserId) {
      console.log("👤 User changed, resetting notifications state", { from: this.lastUserId, to: numericUserId });
      this.recentCount.set(0);
      this.comments.set([]);
      this.lastUserId = numericUserId;

      // Seed unread from storage for the new user for immediate UI
      const seeded = this.loadUnreadForUser(numericUserId);
      if (seeded != null) {
        this.recentCount.set(seeded);
      }
    }
    this.isFetching = true;

    try {
      // Always fetch fresh data -- removed early return that blocked refresh()
      // Check if online
      if (navigator.onLine) {
        // Online: Fetch from API
        await this.fetchFromAPI(numericUserId, token, tokenType);
      } else {
        // Offline: Load from cache
        await this.loadFromCache(numericUserId);
      }
    } catch (error: any) {
      console.warn('Failed to fetch comment notifications:', error);
      
      // Try loading from cache as fallback
      try {
        await this.loadFromCache(numericUserId);
      } catch (cacheError) {
        console.warn('Failed to load from cache:', cacheError);
        this.recentCount.set(0);
        this.comments.set([]);
      }
    } finally {
      this.isFetching = false;
    }
  }

  /**
   * Fetch comment notifications from API and cache them
   */
  private async fetchFromAPI(userId: number, token: string, tokenType: string): Promise<void> {
    // Calculate fromDate as 24 hours ago
    const fromDate = new Date();
    fromDate.setHours(fromDate.getHours() - 24);
    
    const apiUrl = `${environment.apiBaseUrl}/api/v1/passports/comments`;
    const headers = { 
      'Authorization': `${tokenType} ${token}`, 
      'Content-Type': 'application/json' 
    };
    const body = {
      UserId: userId,
      FromDate: fromDate.toISOString()
    };

    const response = await firstValueFrom(
      this.http.post<CommentNotification[]>(apiUrl, body, { headers })
    );
    
    // Handle null or undefined response
    const allComments = (response && Array.isArray(response)) ? response : [];
    this.comments.set(allComments);

    // Count ALL comments from last 24 hours (backend already filtered by fromDate)
    const totalCount = allComments.length;
    this.recentCount.set(totalCount);
    this.saveUnreadForUser(userId, totalCount);

    // Cache the data for offline use (always, regardless of viewport size)
    try {
      await this.offlineStorage.saveCommentNotifications(userId, allComments);
    } catch (cacheError) {
      console.warn('Failed to cache comment notifications:', cacheError);
    }
  }

  /**
   * Load comment notifications from cache
   */
  private async loadFromCache(userId: number): Promise<void> {
    const cachedNotifications = await this.offlineStorage.getCommentNotifications(userId);
    
    if (cachedNotifications && cachedNotifications.length > 0) {
      this.comments.set(cachedNotifications);

      // Count ALL cached comments
      const totalCount = cachedNotifications.length;
      this.recentCount.set(totalCount);
      if (userId != null) this.saveUnreadForUser(userId, totalCount);
    } else {
      this.recentCount.set(0);
      this.comments.set([]);
    }
  }

  /**
   * Refreshes the unread count - to be called after comments are added or marked as read
   */
  async refresh(): Promise<void> {
    await this.fetchUnreadCount();
  }

  /**
   * Gets all comments from the last 24 hours
   */
  getComments(): CommentNotification[] {
    return this.comments();
  }

  /**
   * Manually set comments (useful for mock data)
   */
  setComments(comments: CommentNotification[]): void {
    this.comments.set(comments);
    this.recentCount.set(comments.length);
    
    const userID = localStorage.getItem('userID');
    if (userID) {
      const numericUserId = Number.parseInt(userID, 10);
      if (Number.isFinite(numericUserId)) {
        this.saveUnreadForUser(numericUserId, comments.length);
      }
    }
  }

  /**
   * Seed unreadCount from localStorage for current user (call at login success if needed)
   */
  seedFromStorage(): void {
    const userID = localStorage.getItem('userID');
    const numericUserId = userID ? Number.parseInt(userID, 10) : Number.NaN;
    if (!Number.isFinite(numericUserId)) return;
    const seeded = this.loadUnreadForUser(numericUserId);
    if (seeded != null) this.recentCount.set(seeded);
  }

  /**
   * Explicitly set unread count for current user and persist it (useful on login/logout flows)
   */
  setUnreadCountPersisted(count: number): void {
    const userID = localStorage.getItem('userID');
    const numericUserId = userID ? Number.parseInt(userID, 10) : Number.NaN;
    if (!Number.isFinite(numericUserId)) return;
    this.recentCount.set(count);
    this.saveUnreadForUser(numericUserId, count);
  }

  /**
   * Clear unread count for current user (e.g., at logout)
   */
  clearForCurrentUser(): void {
    const userID = localStorage.getItem('userID');
    const numericUserId = userID ? Number.parseInt(userID, 10) : Number.NaN;
    this.recentCount.set(0);
    this.comments.set([]);
    if (Number.isFinite(numericUserId)) {
      try { localStorage.removeItem(this.getStorageKey(numericUserId)); } catch {
        // Ignore localStorage errors (e.g., in private browsing mode)
      }
      try { localStorage.removeItem(`unreadCount:${numericUserId}`); } catch {
        // Ignore localStorage errors (e.g., in private browsing mode)
      }
    }
  }
}

